import cx from "classnames";
import uniqueId from "lodash.uniqueid";
import { arrayOf, bool, oneOf, shape, string } from "prop-types";
import { useEffect, useState, useCallback } from "react";
import Modal from "../../../../Common/js/components/Modal/Modal.jsx";
import Tabs from "../Tabs/Tabs.jsx";
import { baseCategories } from "./data/baseCategories.js";
import { StuntDriversProvider } from "./StuntDriversContext.jsx";
import StuntDriversDiagram from "./StuntDriversDiagram.jsx";
import StuntDriversInfo from "./StuntDriversInfo.jsx";
import StuntDriversNavigation from "./StuntDriversNavigation.jsx";

const propTypes = {
  callToActionText: string,
  countries: arrayOf(
    shape({
      id: string,
      name: string,
      isEnabled: bool,
      categories: arrayOf(
        shape({
          id: string,
          driverCategoryId: string,
          isEnabled: bool,
          content: string,
          sections: arrayOf(
            shape({
              id: string,
              isEnabled: bool,
              content: string,
            }),
          ),
        }),
      ).isRequired,
      files: shape({
        singleDiagram: string.isRequired,
        expandedDiagram: string.isRequired,
      }),
    }),
  ),
  link: shape({ url: string, text: string }),
  subtitle: string,
  title: string,
  type: oneOf(["agnostic", "single", "multiple"]).isRequired,
};

const StuntDrivers = (props) => {
  const { callToActionText, countries, link, subtitle, title, type } = props;

  const [isModalOpen, toggleModalOpen] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState();
  const [activeSectionId, setActiveSectionId] = useState();
  const [currentCategory, setCurrentCategory] = useState();
  const [currentSection, setCurrentSection] = useState();
  const [activeCountry, setActiveCountry] = useState(
    type === "multiple" ? countries.find((cty) => cty.isEnabled) : countries[0],
  );
  const [mergedCategories, setMergedCategories] = useState();

  useEffect(() => {
    // When a Country is set or updated as active Country recalculates
    // the category and section information into `mergedCategories`
    activeCountry &&
      setMergedCategories(
        baseCategories
          .map((cat) => {
            const categoryData = activeCountry.categories.find(
              (c) => c.id === cat.id,
            );
            if (!categoryData || !categoryData.isEnabled) return null;

            const { sections, ...rest } = categoryData;

            const mergedSections =
              cat.sections &&
              cat.sections.map((sect) => {
                const sectionsExtraData =
                  (sections && sections.find((s) => s.id === sect.id)) || {};

                return {
                  ...sect,
                  ...sectionsExtraData,
                };
              });

            return {
              ...cat,
              ...rest,
              sections: mergedSections || undefined,
            };
          })
          .filter((cat) => cat !== null),
      );
  }, [activeCountry, countries, type]);

  useEffect(() => {
    // When the active category changes update the current category data in state
    if (!activeCategoryId) return;

    const category = mergedCategories.find((cat) => cat.id == activeCategoryId);
    setCurrentCategory(category);
  }, [activeCategoryId, mergedCategories]);

  useEffect(() => {
    // When the active section changes update the current section data in state
    if (!activeSectionId || !currentCategory) return;

    // if current/passed section is not enabled, find first enabled in category
    // (needed when navigating through the NavigationGraphic)
    let section = {};
    if (
      currentCategory.sections[activeSectionId - 1] &&
      currentCategory.sections[activeSectionId - 1].isEnabled
    ) {
      section = currentCategory.sections[activeSectionId - 1];
    } else {
      section =
        currentCategory.sections &&
        currentCategory.sections.find((section) => section.isEnabled);
    }
    setCurrentSection(section);
  }, [activeSectionId, currentCategory]);

  const getCategoriesWithSections = useCallback(() => {
    if (mergedCategories) {
      return type === "agnostic"
        ? mergedCategories.filter((cat) => cat.isEnabled)
        : mergedCategories.filter(
            (cat) =>
              cat.isEnabled &&
              cat.sections &&
              cat.sections.filter((s) => s.isEnabled).length,
          );
    } else return [];
  }, [mergedCategories, type]);

  const handleSelect = (categoryId, sectionId) => {
    setActiveCategoryId(categoryId);
    setActiveSectionId(sectionId);
    toggleModalOpen(true);
  };

  const handleCountrySelect = (countryIndex) => {
    setActiveCategoryId(null);
    setActiveSectionId(null);
    setCurrentCategory(null);
    setCurrentSection(null);
    setActiveCountry(countries[countryIndex]);
  };

  const classes = cx("stunt-drivers", "container", {
    "stunt-drivers--agnostic": type === "agnostic",
    "stunt-drivers--single": type === "single",
    "stunt-drivers--multiple": type === "multiple",
  });

  if (!activeCountry) return null;

  const isPdfReport = document
    .querySelector("body")
    .classList.contains("pdf-report");

  //if the page is dispayed for report pdf ?view=pdf
  if (isPdfReport) {
    const categoriesWithSections = getCategoriesWithSections();
    return categoriesWithSections && categoriesWithSections.length > 0 ? (
      <div>
        {title && <h2>{title}</h2>}
        {subtitle && <h3 className="h3">{subtitle}</h3>}
        <div>
          {categoriesWithSections.map((cat, index) => (
            <div key={index}>
              {cat.name && <h4>{cat.name}</h4>}
              {cat.content && (
                <div dangerouslySetInnerHTML={{ __html: cat.content }}></div>
              )}
              {cat.sections &&
                cat.sections.map((section, index) => (
                  <div key={index}>
                    <div>
                      {section.title && <strong>{section.title}</strong>}
                    </div>
                    {section.content && (
                      <div
                        dangerouslySetInnerHTML={{ __html: section.content }}
                      ></div>
                    )}
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
    ) : null;
  }

  if (!isPdfReport)
    return (
      <StuntDriversProvider
        value={{
          activeCategoryId,
          activeSectionId,
          currentCategory,
          currentSection,
          getCategoriesWithSections,
          handleSelect,
          mergedCategories,
        }}
      >
        <div className={classes}>
          {title && (
            <h2 className="stunt-drivers__title component-title">{title}</h2>
          )}
          {subtitle && <p className="stunt-drivers__subtitle">{subtitle}</p>}

          {type === "multiple" ? (
            <Tabs selectFirstActive handleTabSelect={handleCountrySelect}>
              {countries &&
                countries.map((tab) => (
                  <div
                    label={tab.name}
                    disabled={!tab.isEnabled}
                    key={uniqueId("tab-content-")}
                  >
                    <StuntDriversDiagram
                      callToActionText={callToActionText}
                      categories={tab.categories}
                      country={tab}
                      handleSelect={handleSelect}
                      isExpanded
                      link={link}
                      type={type}
                    />
                  </div>
                ))}
            </Tabs>
          ) : (
            <StuntDriversDiagram
              callToActionText={callToActionText}
              country={activeCountry}
              handleSelect={handleSelect}
              link={link}
              type={type}
            />
          )}

          {/* Render Modal */}
          <Modal
            noPadding
            isOpen={isModalOpen}
            onClose={() => {
              toggleModalOpen(false);
            }}
            title={false}
          >
            {activeCategoryId && (
              <div className="stunt-drivers__modal">
                <div className="stunt-drivers__modal-navigation">
                  {activeCategoryId && <StuntDriversNavigation />}
                </div>
                <div className="stunt-drivers__modal-content">
                  {activeCategoryId && <StuntDriversInfo />}
                </div>
              </div>
            )}
          </Modal>
        </div>
      </StuntDriversProvider>
    );
};

StuntDrivers.propTypes = propTypes;
export default StuntDrivers;
